import {
    ImpersonationState,
    ImpersonationInfo,
    FetchState,
    FeatureFlags,
    HsabAccountType
} from "@/shared/types";
import * as UserService from "@/shared/api/userService";
import * as BankService from "@/shared/api/bankService";
import { Home } from "@/shared/route-names";
import {
    AccountsSummary
} from "@/employee/route-names";
import { defineStore } from "pinia";
import _ from "lodash";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useUserStore } from "@/shared/store/user";
import { getStoragePaths } from "@/shared/storeHelper";

export const initialState: ImpersonationState = {
    impersonation: {} as ImpersonationInfo,
    isActive: false,
    isStarting: false,
    initiateResponseFetchState: FetchState.Uninitialized,
    impersonationInitiateData: null
};

export const useImpersonatorStore = defineStore("impersonator", {
    persist: {
        storage: localStorage,
        paths: getStoragePaths("impersonator"),
    },
    state: () => (_.cloneDeep(initialState)),
    getters: {
        impersonatedName: state => {
            if (state.impersonation && state.impersonation.userDetail &&
                state.impersonation.userDetail.firstName !== "") {
                return `${state.impersonation.userDetail.firstName} ${state.impersonation.userDetail.lastName}`;
            }
            return "";
        },
        impersonationAuthType: state => {
            if (state.impersonation && state.impersonation.userDetail) {
                return state.impersonation.userDetail.provider;
            }
            return null;
        },
        impersonationIsActive: state => state.isActive,
        impersonationIsStarting: state => state.isStarting,
        initiateResponseLoaded: state => state.initiateResponseFetchState === FetchState.Loaded,
        impersonationToken: state => state.impersonationInitiateData
            ? state.impersonationInitiateData.impersonationToken : null
    },
    actions: {
        setImpersonation (impersonation) {
            this.impersonation = impersonation;
        },
        clearImpersonation () {
            this.impersonation = {} as ImpersonationInfo;
            this.isActive = false;
            this.isStarting = false;
        },
        setImpersonationActive (value: boolean) {
            this.isActive = value;
        },
        setInitiateResponseFetchState (fetchState: FetchState) {
            this.initiateResponseFetchState = fetchState;
        },
        setImpersonationInitiateResponse (response) {
            this.impersonationInitiateData = response;
        },
        setImpersonationStarting (value: boolean) {
            this.isStarting = value;
        },
        startPartnerImpersonation (
            { impersonationInfo, router, forcePageReload }) {
            this.setImpersonation(impersonationInfo);
            this.setImpersonationActive(true);
            const userStore = useUserStore();
            const custodianSettingsStore = useCustodianSettingsStore();
            const featureFlagStore = useFeatureFlagStore();
            const ii: ImpersonationInfo = impersonationInfo;
            userStore.activeUserPing();
            userStore.processLogin({
                email: ii.email,
                displayName: `${ii.userDetail.firstName} ${ii.userDetail.lastName}`,
                groups: [ "employee" ],
                phone: ii.userDetail.phone
            }).then(() => {
                this.setImpersonationStarting(false);

                const isHsaBank = custodianSettingsStore.isHsaBankCustodian;
                if (isHsaBank) {
                    BankService.getHsabAccountTypes()
                        .then(response => {
                            const hsaAccount = response.data.accounts.filter(
                                t => t.bankAccountType === HsabAccountType.Hsa);
                            const notionalAccounts = response.data.accounts.filter(
                                t => t.bankAccountType === HsabAccountType.Notional);
                            if (hsaAccount.length > 0 && notionalAccounts.length < 1) {
                                router.push({ name: Home, params: { forcePageReload } });
                            } else {
                                router.push({ name: AccountsSummary, params: { forcePageReload } });
                            }
                        });
                } else {
                    router.push({ name: Home, params: { forcePageReload } });
                }
            });
        },
        stopImpersonation () {
            this.clearImpersonation();
            return Promise.resolve();
        },
        retrieveInitiateResponse (token: string): Promise<any> {
            this.setImpersonationStarting(true);
            this.setInitiateResponseFetchState(FetchState.Loading);
            return UserService.getInitiateImpersonationResponse(token)
                .then(response => {
                    this.setImpersonationInitiateResponse(response.data);
                    this.setInitiateResponseFetchState(FetchState.Loaded);
                });
        },
        redirectToImpersonation (
            params: {
                targetCognitoUsername: string, newTab: Window, partnerId: number
            }): Promise<any> {
            return UserService.getImpersonationUrl(params.targetCognitoUsername, params.partnerId)
                .then(response => {
                    params.newTab.location.href = response.data;
                });
        }
    }
});

